// src/Dashboard/pages/teacher/SuiviCompetitions/components/CompetitionRegisterDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTimes,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";
import { registerAdherent } from "../../../../../services/eventServices";

import {
  CATE_EVENTS,
  formatWeight,
  getAdherentCompetitionCategories,
  getAdherentName,
  getAdherentPhoto,
  getBirthYear,
  uniqSorted,
} from "../utils/suiviCompetitionsUtils";

const STOPPED_STATUSES = [
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié et Arrêt",
];

const STATUS_OPTIONS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const getPreviousSeason = (season) => {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start - 1}-${end - 1}`;
};

const getNextSeason = (season) => {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start + 1}-${end + 1}`;
};

const getGroupsLabel = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
};

const normalizeRegisteredIds = (registeredIds) => {
  if (!registeredIds) return new Set();

  if (registeredIds instanceof Set) {
    return new Set(Array.from(registeredIds).map((id) => String(id)));
  }

  if (Array.isArray(registeredIds)) {
    return new Set(registeredIds.map((id) => String(id)));
  }

  return new Set();
};

function AdherentRegisterCard({
  adherent,
  season,
  selected,
  alreadyRegistered,
  onToggle,
}) {
  const photo = getAdherentPhoto(adherent);
  const name = getAdherentName(adherent);
  const groupsLabel = getGroupsLabel(adherent);
  const birthYear = getBirthYear(adherent?.date_naissance);

  const { ageCategory, weightCategory } = getAdherentCompetitionCategories(
    adherent,
    season
  );

  return (
    <button
      type="button"
      disabled={alreadyRegistered}
      onClick={() => {
        if (!alreadyRegistered) onToggle(adherent.id);
      }}
      className={`relative flex min-h-[118px] items-start gap-3 rounded-[24px] border p-3 text-left transition ${
        selected
          ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-300/50 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:ring-indigo-500/20"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
      } ${alreadyRegistered ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <img
        src={photo}
        alt={name}
        className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
        onError={(event) => {
          event.currentTarget.src = "/img/user_avatar.jpg";
        }}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
          {name}
        </p>

        <div className="mt-1 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/50">
            {ageCategory || "Inconnu"}
          </span>

          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/50">
            {birthYear || "Année ?"}
          </span>

          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/50">
            {weightCategory || "Poids ?"}
          </span>
        </div>

        <p className="mt-2 line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400">
          {groupsLabel || "Sans groupe"}
        </p>

        <p className="mt-1 text-[11px] text-slate-400">
          Poids enregistré : {formatWeight(adherent?.poids)}
        </p>

        {alreadyRegistered && (
          <p className="mt-2 inline-flex rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Déjà inscrit
          </p>
        )}
      </div>

      {selected && (
        <span className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-xl bg-indigo-600 text-white shadow">
          <FaUserCheck />
        </span>
      )}
    </button>
  );
}

export default function CompetitionRegisterDrawer({
  isOpen,
  event,
  registeredIds,
  onClose,
  onRegistered,
}) {
  const [adherents, setAdherents] = useState([]);
  const [seasons, setSeasons] = useState([]);

  const [search, setSearch] = useState("");
  const [season, setSeason] = useState("");
  const [statusFilter, setStatusFilter] = useState("Licencié");
  const [ageCategoryFilter, setAgeCategoryFilter] = useState("");
  const [birthYearFilter, setBirthYearFilter] = useState("");
  const [onlyNotRegistered, setOnlyNotRegistered] = useState(true);
  const [hideStopped, setHideStopped] = useState(true);

  const [selectedIds, setSelectedIds] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const registeredSet = useMemo(
    () => normalizeRegisteredIds(registeredIds),
    [registeredIds]
  );

  useEffect(() => {
    if (!isOpen) return;

    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setSearch("");
        setSelectedIds(new Set());

        const [list, settingsData] = await Promise.all([
          getAllAdherentsListWithGroup(),
          getSettings(),
        ]);

        if (!alive) return;

        setAdherents(Array.isArray(list) ? list : []);

        const settings = Array.isArray(settingsData)
          ? settingsData[0]
          : settingsData;

        const activeSeason = settings?.saison_active || "";

        if (activeSeason) {
          setSeasons(
            [
              getPreviousSeason(activeSeason),
              activeSeason,
              getNextSeason(activeSeason),
            ].filter(Boolean)
          );

          setSeason(activeSeason);
        }
      } catch (error) {
        console.error("Erreur chargement adhérents :", error);
        toast.error("Erreur lors du chargement des adhérents.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const enrichedAdherents = useMemo(() => {
    return (adherents || []).map((adherent) => {
      const categories = getAdherentCompetitionCategories(adherent, season);

      return {
        ...adherent,
        _name: getAdherentName(adherent),
        _groupsLabel: getGroupsLabel(adherent),
        _birthYear: categories.birthYear,
        _ageCategory: categories.ageCategory,
        _weightCategory: categories.weightCategory,
        _alreadyRegistered: registeredSet.has(String(adherent.id)),
      };
    });
  }, [adherents, season, registeredSet]);

  const ageCategoryOptions = useMemo(() => {
    const found = uniqSorted(
      enrichedAdherents.map((adherent) => adherent._ageCategory)
    );

    const ordered = CATE_EVENTS.filter((category) => found.includes(category));
    const others = found.filter((category) => !ordered.includes(category));

    return [...ordered, ...others];
  }, [enrichedAdherents]);

  const birthYearOptions = useMemo(() => {
    return uniqSorted(
      enrichedAdherents
        .map((adherent) => adherent._birthYear)
        .filter(Boolean)
    );
  }, [enrichedAdherents]);

  const filteredAdherents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return enrichedAdherents
      .filter((adherent) => {
        const matchesSearch =
          !query ||
          [
            adherent._name,
            adherent.licence_number,
            adherent.statut_inscription,
            adherent._groupsLabel,
            adherent._ageCategory,
            adherent._birthYear,
            adherent._weightCategory,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(query);

        const matchesSeason = !season || adherent.saison_active === season;

        const matchesStatus =
          !statusFilter || adherent.statut_inscription === statusFilter;

        const matchesAgeCategory =
          !ageCategoryFilter || adherent._ageCategory === ageCategoryFilter;

        const matchesBirthYear =
          !birthYearFilter ||
          String(adherent._birthYear) === String(birthYearFilter);

        const matchesRegistered =
          !onlyNotRegistered || !adherent._alreadyRegistered;

        const matchesStopped =
          !hideStopped || !STOPPED_STATUSES.includes(adherent.statut_inscription);

        return (
          matchesSearch &&
          matchesSeason &&
          matchesStatus &&
          matchesAgeCategory &&
          matchesBirthYear &&
          matchesRegistered &&
          matchesStopped
        );
      })
      .sort((a, b) => a._name.localeCompare(b._name, "fr"));
  }, [
    enrichedAdherents,
    search,
    season,
    statusFilter,
    ageCategoryFilter,
    birthYearFilter,
    onlyNotRegistered,
    hideStopped,
  ]);

  const selectedAdherents = useMemo(() => {
    return enrichedAdherents.filter((adherent) => selectedIds.has(adherent.id));
  }, [enrichedAdherents, selectedIds]);

  const toggleOne = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const selectVisible = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);

      filteredAdherents.forEach((adherent) => {
        if (!adherent._alreadyRegistered) {
          next.add(adherent.id);
        }
      });

      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("Licencié");
    setAgeCategoryFilter("");
    setBirthYearFilter("");
    setOnlyNotRegistered(true);
    setHideStopped(true);
  };

  const handleSubmit = async () => {
    if (!event?.id) {
      toast.error("Événement manquant.");
      return;
    }

    const ids = Array.from(selectedIds)
      .map(Number)
      .filter((id) => !registeredSet.has(String(id)));

    if (ids.length === 0) {
      toast.error("Veuillez sélectionner au moins un adhérent.");
      return;
    }

    try {
      setSaving(true);

      const results = await Promise.allSettled(
        ids.map((adherentId) => registerAdherent(event.id, adherentId))
      );

      const success = results.filter((result) => result.status === "fulfilled")
        .length;
      const failed = results.length - success;

      if (success && !failed) {
        toast.success(
          `${success} adhérent${success > 1 ? "s" : ""} inscrit${
            success > 1 ? "s" : ""
          }.`
        );
      } else if (success && failed) {
        toast.warn(`${success} inscription(s), ${failed} erreur(s).`);
      } else {
        toast.error("Aucune inscription n’a pu être enregistrée.");
      }

      setSelectedIds(new Set());
      await onRegistered?.();

      if (success > 0 && failed === 0) {
        onClose?.();
      }
    } catch (error) {
      console.error("Erreur inscription :", error);
      toast.error("Erreur lors de l’inscription.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Inscription compétition
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {event?.titre || "Événement"}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-2xl bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60 sm:inline-flex">
              <FaUsers />
              {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
              <FaFilter className="text-indigo-500" />
              Filtres d’inscription
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
              <div className="xl:col-span-2">
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Recherche
                </label>

                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Nom, prénom, licence, groupe..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>

              <SelectField
                label="Saison"
                value={season}
                onChange={(value) => {
                  setSeason(value);
                  setSelectedIds(new Set());
                }}
                options={seasons}
                emptyLabel="Toutes"
              />

              <SelectField
                label="Statut"
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
                emptyLabel="Tous"
              />

              <SelectField
                label="Catégorie"
                value={ageCategoryFilter}
                onChange={setAgeCategoryFilter}
                options={ageCategoryOptions}
                emptyLabel="Toutes"
              />

              <SelectField
                label="Année"
                value={birthYearFilter}
                onChange={setBirthYearFilter}
                options={birthYearOptions}
                emptyLabel="Toutes"
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <FilterToggle
                active={onlyNotRegistered}
                onClick={() => setOnlyNotRegistered((value) => !value)}
                label="Masquer déjà inscrits"
              />

              <FilterToggle
                active={hideStopped}
                onClick={() => setHideStopped((value) => !value)}
                label="Masquer les arrêts"
              />

              <button
                type="button"
                onClick={selectVisible}
                className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700"
              >
                <FaCheck />
                Sélectionner visibles
              </button>

              <button
                type="button"
                onClick={clearSelection}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Vider
              </button>

              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                Réinitialiser filtres
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Adhérents disponibles
              </p>

              <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
                {loading
                  ? "Chargement..."
                  : `${filteredAdherents.length} résultat${
                      filteredAdherents.length > 1 ? "s" : ""
                    }`}
              </h3>
            </div>

            {selectedAdherents.length > 0 && (
              <div className="rounded-2xl bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
                {selectedAdherents.length} à inscrire
              </div>
            )}
          </div>

          {loading ? (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {[...Array(9)].map((_, index) => (
                <div
                  key={index}
                  className="h-28 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : filteredAdherents.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
              Aucun adhérent disponible à inscrire avec ces filtres.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filteredAdherents.map((adherent) => (
                <AdherentRegisterCard
                  key={adherent.id}
                  adherent={adherent}
                  season={season}
                  selected={selectedIds.has(adherent.id)}
                  alreadyRegistered={adherent._alreadyRegistered}
                  onToggle={toggleOne}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Sélection :{" "}
              <span className="font-bold text-slate-900 dark:text-white">
                {selectedIds.size}
              </span>{" "}
              adhérent{selectedIds.size > 1 ? "s" : ""}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || selectedIds.size === 0}
                className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaPlus />
                {saving
                  ? "Inscription..."
                  : `Inscrire ${selectedIds.size || ""}`}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function SelectField({ label, value, onChange, options = [], emptyLabel }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      >
        <option value="">{emptyLabel}</option>

        {(options || [])
          .filter((item) => item !== null && item !== undefined)
          .map((item) => (
            <option key={String(item || "empty")} value={item}>
              {item || emptyLabel}
            </option>
          ))}
      </select>
    </div>
  );
}

function FilterToggle({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-bold ring-1 transition ${
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-current" : "bg-slate-400"
        }`}
      />
      {label}
    </button>
  );
}